import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import StillButton from './ui/StillButton';
import { useOverlay } from '../App';
import { useEffect, useState } from 'react';

const ResourcesOverlay = () => {
    const navigate = useNavigate();
    const { toggleOverlay } = useOverlay();
    const [isMobile, setIsMobile] = useState(false);

    // Check for mobile view
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 800);
        };
        
        // Check immediately
        checkMobile();
        
        // Add resize listener
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);


    // Explicitly hide the header when this component mounts
    useEffect(() => {
        toggleOverlay(false);
        
        // Ensure header remains hidden when component unmounts
        return () => toggleOverlay(false);
    }, [toggleOverlay]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 1,
                staggerChildren: 0.5,
            },
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 5 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 2,
                ease: "easeOut"
            }
        }
    };

    const handleClose = () => {
        navigate('/space');
    };

    return (
        <div className="fixed inset-0 flex flex-col bg-black bg-opacity-40">
            <motion.div 
                className="min-h-screen w-full flex flex-col items-center px-10 mt-20 lg:mt-20 "
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >

                    <motion.div 
                        className="space-y-10 overflow-y-auto overflow-x-hidden pb-40 md:pb-32"
                        variants={itemVariants}
                    >

                        <div className="text-still-white text-base text-center pt-10 sm:pt-20 lg:pt-32 xl:pt-32 2xl:pt-32 lg:text-base max-w-[750px] mx-auto pb-6">
                            <motion.p variants={itemVariants}>
                            Losing a baby in early pregnancy is a heartbreak no one should have to carry alone.<br /> Whether you're looking for a space to share your grief, connect with others who understand, show your support or find professional support, this page is here to guide you to the help you need.
                            </motion.p>
                        </div>

                        <div className="text-still-white text-base text-center pt-0 sm:pt-0 lg:pt-0 xl:pt-0 2xl:pt-0 lg:text-base max-w-[1100px] mx-auto pb-6">
                            <div className="grid grid-cols-1 md:grid-cols-1 gap-20">
                                <div>
                                    <h2 className="text-lg font-bold mb-4 text-still-pink-dark">
                                        Still a Father's Day Card
                                    </h2>
                                    <motion.p variants={itemVariants}>
                                        Show your support this Father's Day, by sending a loved one a digital card. <br />Simply click on the button below to download your shareable card.
                                    </motion.p>
                                    <StillButton
                                        onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = '/downloads/Still_Loved_Digital_Fathers_Day_Card.pdf';
                                            link.download = 'Still_Loved_Digital_Fathers_Day_Card.pdf';
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        }}
                                        className="mt-6"
                                    >
                                        Download Card
                                    </StillButton>
                                </div>
                            </div>
                        </div>
                        <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-6 mt-14"></div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 text-still-white text-base lg:text-base max-w-[1100px] mx-auto">
                            <div className="flex flex-col">
                                <h2 className="text-lg font-bold mb-4 text-still-pink-dark">
                                Still Parents at The Whitworth Gallery - <br />Creative support through art
                                </h2>
                                <p className="text-still-gray-light mb-3 leading-relaxed">
                                    Still Parents is the Whitworth's award-winning programme supporting parents who have experienced the loss of a baby in pregnancy or shortly after birth. These free, artist-led sessions provide a space for parents to explore their grief through creative expression, guided by professional artists and supported by Manchester SANDS charity. Through making, sharing, and connecting, Still Parents offers a compassionate, understanding community for those navigating life after loss.
                                </p>
                                <p className="text-lg font-semibold text-still-white mb-1">Learn more and join a session:</p>
                                <div className="mb-6">
                                    <a href="https://www.whitworth.manchester.ac.uk/learn/stillparents/" target="_blank" rel="noopener noreferrer" className="text-still-blue hover:text-still-pink transition-colors break-words">www.whitworth.manchester.ac.uk/learn/stillparents/</a>
                                </div>
                            </div>

                            <div className="flex flex-col">
                                <h2 className="text-lg font-bold mb-4 text-still-pink-dark">
                                Manchester SANDS - <br />Support for bereaved parents
                                </h2>
                                <p>
                                Manchester Sands is part of the national Sands charity, dedicated to supporting families who have experienced the loss of a baby. They offer local support groups, helplines, and remembrance events, as well as providing guidance for healthcare professionals to improve bereavement care.
                                </p>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-col">
                                        <p className="font-bold mt-4">
                                        Helpline:
                                        </p>
                                        <a href="tel:0808 164 3332" className="text-still-blue hover:text-still-pink transition-colors">0808 164 3332</a>
                                    </div>
                                    <div className="flex flex-col">
                                        <p className="font-bold mt-4">
                                        Email:
                                        </p>
                                        <a href="mailto:helpline@sands.org.uk" className="text-still-blue hover:text-still-pink transition-colors break-words">helpline@sands.org.uk</a>
                                    </div>
                                    <div className="flex flex-col">
                                        <p className="font-bold mt-4">
                                        Find support near you:
                                        </p>
                                        <a href="https://www.sands.org.uk/" target="_blank" rel="noopener noreferrer" className="text-still-blue hover:text-still-pink transition-colors break-words">www.sands.org.uk</a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-10">
                            <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-6 mt-14"></div>
                            <div className="flex flex-col items-center gap-4">
                                <h2 className="text-lg font-bold mt-6 text-still-pink-dark text-center">
                                    Finding emotional & mental health support
                                </h2>
                                <p className="text-still-white text-center max-w-[750px]">
                                If you're struggling, please know that support is available. You are not alone. Your grief is real, your love is real, and support is here for you when you need it. Here are some places that can help:
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 text-still-white text-base lg:text-base max-w-[1100px] mx-auto mt-10 mb-20">
                                <div className="flex flex-col gap-4">
                                    <h3 className="font-bold mt-4 text-still-pink-dark">
                                        Tommy's Baby Loss Support
                                    </h3>
                                    <p>
                                    Provides expert advice, a free midwife-led helpline, and online resources. <a href="https://www.tommys.org" target="_blank" rel="noopener noreferrer" className="text-still-blue hover:text-still-pink transition-colors break-words">www.tommys.org</a>
                                    </p>
                                </div>
                                <div className="flex flex-col gap-4">
                                    <h3 className="font-bold mt-4 text-still-pink-dark">
                                        The Miscarriage Association
                                    </h3>
                                    <p>
                                    Support for parents who have experienced miscarriage, ectopic pregnancy, or molar pregnancy. <a href="https://www.miscarriageassociation.org.uk/" target="_blank" rel="noopener noreferrer" className="text-still-blue hover:text-still-pink transition-colors break-words">www.miscarriageassociation.org.uk</a>
                                    </p>
                                    <div className="mt-2">
                                        <strong>Helpline:</strong> <a href="tel:01924 200799" className="text-still-blue hover:text-still-pink transition-colors">01924 200799</a>
                                    </div>
                                </div>
                            </div>  
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 text-still-white text-base lg:text-base max-w-[1100px] mx-auto mt-10">
                                <div className="flex flex-col gap-4">
                                    <h3 className="font-bold mt-4 text-still-pink-dark">
                                        Cruse Bereavement Support
                                    </h3>
                                    <p>
                                    Free grief counselling and bereavement support. <a href="https://www.cruse.org.uk/" target="_blank" rel="noopener noreferrer" className="text-still-blue hover:text-still-pink transition-colors break-words">www.cruse.org.uk</a>
                                    </p>
                                    <div className="mt-2">
                                        <strong>Helpline:</strong> <a href="tel:0808 808 1677" className="text-still-blue hover:text-still-pink transition-colors">0808 808 1677</a>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-4">
                                    <h3 className="font-bold mt-4 text-still-pink-dark">
                                        Mind - Mental Health Support
                                    </h3>
                                    <p>
                                    If you're struggling with grief and mental health, Mind offers a range of resources and helplines. <a href="https://www.mind.org.uk/" target="_blank" rel="noopener noreferrer" className="text-still-blue hover:text-still-pink transition-colors break-words">www.mind.org.uk</a>
                                    </p>
                                    <div className="mt-2">
                                        <strong>Infoline:</strong> <a href="tel:0300 123 3393" className="text-still-blue hover:text-still-pink transition-colors">0300 123 3393</a>
                                    </div>
                                </div>
                            </div>

                            <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-6 mt-14"></div>
                            <div className="flex flex-col items-center gap-4 mb-60">
                                <h2 className="text-lg font-bold mt-6 text-still-pink-dark text-center">
                                    Your Support
                                </h2>
                                <p className="text-still-white text-center max-w-[750px]">
                                    We know the power of voices in creating real change. Your ability to reach and move people is incredible, and we would be honoured if you would help us share this message. Whether it's a post, a story, or a few simple words, your support could help grieving parents feel seen, comforted, and, above all, help remember all those <span className="whitespace-nowrap">Still Loved</span>.
                                    <br /><br />
                                    Click on the button below to get everything you need - the story behind Still Loved, suggested messaging, and beautiful visuals to share. But most of all, we hope you'll find something meaningful. Something that moves you. Something that reminds us all that love, once created, never fades.
                                </p>
                                <StillButton
                                    onClick={() => {
                                        window.open('/downloads/Still_Loved_Resources.zip', '_blank');
                                    }}
                                    className="mt-6"
                                >
                                Download Assets
                                </StillButton>
                            </div>
                        </div>

                    </motion.div>
                
            </motion.div>
            
            {/* Close button - fixed at bottom with proper spacing */}
            <motion.div 
                className={`fixed bottom-0 left-0 right-0 flex justify-center space-x-4 pointer-events-auto ${isMobile ? 'mb-0' : 'mb-12 sm:mb-0 md:mb-0 lg:mb-0'} z-50 pt-28 pb-20 py-4 bg-gradient-to-t from-black via-black to-transparent h-22`}
                variants={itemVariants}
            >
                <StillButton
                    onClick={handleClose}
                    aria-label="Close"
                    className="w-auto mb-0"
                >
                    CLOSE
                </StillButton>
            </motion.div>
        </div>
    );
};

export default ResourcesOverlay; 